import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ModalHeader, closeOnlyViaButton, modalHeaderBtnSx } from '../chatMessageStyles';
import AreDisclaimer from '../AreDisclaimer';
import { AudioPlayer, LoadingIndicator, SUMMARY_LOADING_STEPS } from '../AudioSummaryPanel';
import AudioBuilderVoice from './AudioBuilderVoice';
import { createCard, listClients, previewCard, updateCard } from './api';
import { CardIcon, WaveIcon, blankDraft, blockSpec, draftOf, iconOf, missingFields } from './cardModel';
import './audio.css';

const labelSx = { color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '10px', fontWeight: 600, mb: 0.75 };
const toggleSx = {
  flexWrap: 'wrap', gap: 0.75,
  '& .MuiToggleButton-root': {
    border: '1px solid rgba(255, 255, 255, 0.18) !important', borderRadius: '16px !important', textTransform: 'none',
    px: 1.5, py: 0.4, fontSize: '13px', color: 'text.secondary', bgcolor: 'background.paper',
    '&.Mui-selected': { color: 'primary.main', borderColor: 'rgba(25, 208, 239, 0.55) !important', bgcolor: 'rgba(25, 208, 239, 0.12)' },
  },
};

function Section({ label, children }) {
  return (
    <Box sx={{ mb: 2.25 }}>
      <Typography sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

/**
 * Create or change an own audio card. Opens straight into the conversation
 * with Bro (`startWithVoice`) or on the form; both edit the same draft — what
 * Bro builds shows up in the form behind the overlay, and the form can be
 * adjusted by hand afterwards. Saving happens here (button) or in the
 * dialog ("speichern" — the backend stores it and the overlay hands over).
 */
export default function AudioCardBuilder({ card, catalog, sources, language, startWithVoice, onClose, onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => (card ? draftOf(card) : blankDraft(sources)));
  const [voiceOpen, setVoiceOpen] = useState(!!startWithVoice);
  const [clients, setClients] = useState({});
  const [addAnchor, setAddAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState({ loading: false, audio: '', text: '', error: '', showText: false });
  const [dragFrom, setDragFrom] = useState(null);

  const models = Object.keys(sources || {});
  const model = draft.model || (models.length === 1 ? models[0] : null);
  const missing = missingFields({ ...draft, model });
  const icon = iconOf(catalog, draft);
  const available = useMemo(() => (catalog?.blocks || []).filter((b) => b.model === model), [catalog, model]);

  // The draft changed — an old preview no longer matches it.
  const change = useCallback((patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setPreview({ loading: false, audio: '', text: '', error: '', showText: false });
    setError('');
  }, []);

  useEffect(() => {
    if (!model || draft.scope?.type !== 'client' || clients[model]) return;
    listClients(model).then((r) => setClients((c) => ({ ...c, [model]: r.clients || [] })))
      .catch(() => setClients((c) => ({ ...c, [model]: [] })));
  }, [model, draft.scope, clients]);

  const setModel = (m) => {
    if (!m || m === model) return;
    change({ model: m, blocks: draft.blocks.filter((b) => blockSpec(catalog, b.key)?.model === m),
      scope: { type: draft.scope?.type || null, client: null } });
  };
  const setScopeType = (type) => {
    if (!type) return;
    const blocks = type === 'client' ? draft.blocks.filter((b) => !blockSpec(catalog, b.key)?.book_only) : draft.blocks;
    change({ scope: { type, client: type === 'client' ? draft.scope?.client || null : null }, blocks });
  };
  const moveBlock = (from, to) => {
    if (to < 0 || to >= draft.blocks.length || from === to) return;
    const blocks = [...draft.blocks];
    blocks.splice(to, 0, blocks.splice(from, 1)[0]);
    change({ blocks });
  };
  const cycleOption = (i) => {
    const spec = blockSpec(catalog, draft.blocks[i].key);
    if (!spec || spec.options.length < 2) return;
    const ids = spec.options.map((o) => o.id);
    const blocks = draft.blocks.map((b, j) => (j === i ? { ...b, option: ids[(ids.indexOf(b.option) + 1) % ids.length] } : b));
    change({ blocks });
  };
  const addBlock = (spec) => {
    setAddAnchor(null);
    change({ model: model || spec.model, blocks: [...draft.blocks, { key: spec.key, option: spec.options[0].id }] });
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = { ...draft, model };
      const res = card ? await updateCard(card.id, body) : await createCard(body);
      onSaved(res.card, !card);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const playPreview = async () => {
    setPreview({ loading: true, audio: '', text: '', error: '', showText: false });
    try {
      const res = await previewCard({ ...draft, model, scope: draft.scope?.type ? draft.scope : { type: 'book' } }, language);
      setPreview({ loading: false, audio: res.render.audio, text: res.render.text, error: '', showText: false });
    } catch (e) {
      setPreview({ loading: false, audio: '', text: '', error: e.message, showText: false });
    }
  };

  // Quick replies the dialog starts with: examples for a new card, likely
  // changes for an existing one.
  const quickStart = card
    ? [draft.length === 'lang' ? t('audio_quick_shorter') : t('audio_quick_longer'),
      draft.schedule === 'daily' ? t('audio_quick_weekly') : t('audio_quick_daily'), t('audio_quick_play')]
    : [...(sources?.efd ? [t('audio_example_efd_1'), t('audio_example_efd_2')] : []),
      ...(sources?.ppi ? [t('audio_example_ppi')] : [])].slice(0, 3);

  const title = card ? t('audio_edit_title', { name: card.name }) : t('audio_new_title');

  return (
    <Dialog
      open
      onClose={closeOnlyViaButton(onClose)}
      maxWidth={false}
      fullWidth
      sx={{ '& .MuiDialog-container': { alignItems: 'center', justifyContent: 'center' } }}
      PaperProps={{ sx: { borderRadius: '14px', m: 2, width: '100%', maxWidth: 760, height: 'min(840px, calc(100% - 32px))', overflow: 'hidden', position: 'relative' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2.5, pt: 2.5 }}>
          <ModalHeader
            icon={<CardIcon name={icon} />}
            title={title}
            onClose={onClose}
            extra={(
              <Button onClick={() => setVoiceOpen(true)} size="small" startIcon={<WaveIcon size={16} />}
                sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
                {t('audio_talk_to_bro')}
              </Button>
            )}
          />
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2.5, pb: 2.5, pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.25 }}>{t('audio_builder_intro')}</Typography>

          <Section label={t('audio_field_name')}>
            <TextField size="small" fullWidth value={draft.name} placeholder={t('audio_name_placeholder')}
              onChange={(e) => change({ name: e.target.value, name_auto: !e.target.value })}
              inputProps={{ maxLength: 60 }} />
          </Section>

          {models.length > 1 && (
            <Section label={t('audio_field_model')}>
              <ToggleButtonGroup exclusive value={model} onChange={(_, v) => setModel(v)} sx={toggleSx}>
                {models.map((m) => <ToggleButton key={m} value={m}>{t(`audio_model_${m}`)}</ToggleButton>)}
              </ToggleButtonGroup>
            </Section>
          )}

          <Section label={t('audio_field_scope')}>
            <ToggleButtonGroup exclusive value={draft.scope?.type || null} onChange={(_, v) => setScopeType(v)} sx={toggleSx}>
              <ToggleButton value="book">{sources?.[model]?.book_label || t('audio_scope_book')}</ToggleButton>
              <ToggleButton value="client">{t(model === 'ppi' ? 'audio_scope_area' : 'audio_scope_client')}</ToggleButton>
            </ToggleButtonGroup>
            {draft.scope?.type === 'client' && (
              <Autocomplete
                size="small"
                sx={{ mt: 1, maxWidth: 380 }}
                options={clients[model] || []}
                loading={!clients[model]}
                value={draft.scope.client || null}
                onChange={(_, v) => change({ scope: { type: 'client', client: v || null } })}
                renderInput={(params) => <TextField {...params} placeholder={t('audio_client_placeholder')} />}
              />
            )}
          </Section>

          <Section label={t('audio_field_blocks')}>
            {draft.blocks.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>{t('audio_blocks_empty')}</Typography>
            )}
            {draft.blocks.map((b, i) => {
              const spec = blockSpec(catalog, b.key);
              const option = spec?.options.find((o) => o.id === b.option);
              return (
                <Box key={`${b.key}`} draggable
                  onDragStart={() => setDragFrom(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) moveBlock(dragFrom, i); setDragFrom(null); }}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, pl: 0.5, mb: 1, borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.06)', border: 1, borderColor: dragFrom === i ? 'primary.main' : 'divider' }}>
                  <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled', cursor: 'grab' }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', minWidth: 22 }}>#{i + 1}</Typography>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}>{spec?.label || b.key}</Typography>
                  {option && (
                    <Chip size="small" label={option.label} onClick={spec.options.length > 1 ? () => cycleOption(i) : undefined}
                      title={spec.options.length > 1 ? t('audio_option_cycle') : undefined}
                      sx={{ bgcolor: 'rgba(25, 208, 239, 0.08)', color: 'primary.main', border: '1px solid rgba(25, 208, 239, 0.25)', fontSize: '12px' }} />
                  )}
                  <IconButton size="small" disabled={i === 0} onClick={() => moveBlock(i, i - 1)} aria-label={t('audio_move_up')}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                    <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => change({ blocks: draft.blocks.filter((_, j) => j !== i) })}
                    aria-label={t('audio_remove')} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
              );
            })}
            <Button onClick={(e) => setAddAnchor(e.currentTarget)} size="small" startIcon={<AddIcon />}
              sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
              {t('audio_add_block')}
            </Button>
            <Menu anchorEl={addAnchor} open={!!addAnchor} onClose={() => setAddAnchor(null)}>
              {(model ? available : catalog?.blocks || []).map((spec) => {
                const used = draft.blocks.some((b) => b.key === spec.key);
                const blocked = draft.scope?.type === 'client' && spec.book_only;
                return (
                  <MenuItem key={spec.key} disabled={used || blocked} onClick={() => addBlock(spec)} sx={{ fontSize: 14 }}>
                    <CardIcon name={spec.icon} sx={{ fontSize: 18, color: 'primary.main', mr: 1.25 }} />
                    <Box>
                      {spec.label}
                      {(used || blocked || models.length > 1) && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                          {used ? t('audio_block_used') : blocked ? t('audio_block_book_only') : t(`audio_model_${spec.model}`)}
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>
          </Section>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.4fr' }, columnGap: 3 }}>
            <Section label={t('audio_field_length')}>
              <ToggleButtonGroup exclusive value={draft.length} onChange={(_, v) => v && change({ length: v })} sx={toggleSx}>
                {(catalog?.lengths || []).map((l) => <ToggleButton key={l.id} value={l.id}>{l.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Section>
            <Section label={t('audio_field_schedule')}>
              <ToggleButtonGroup exclusive value={draft.schedule} onChange={(_, v) => v && change({ schedule: v })} sx={toggleSx}>
                {(catalog?.schedules || []).map((s) => <ToggleButton key={s.id} value={s.id}>{s.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Section>
          </Box>

          <Section label={t('audio_field_preview')}>
            <Box sx={{ position: 'relative', minHeight: 46 }}>
              {preview.audio ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <AudioPlayer key={preview.audio.slice(-32)} src={`data:audio/mp3;base64,${preview.audio}`}
                      card={draft.name || 'preview'} length="preview" />
                  </Box>
                  <IconButton size="small" onClick={() => setPreview((p) => ({ ...p, showText: !p.showText }))}
                    title={t('showText') || 'Show text'}
                    sx={{ color: preview.showText ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}>
                    <DescriptionIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ) : (
                <Button onClick={playPreview} disabled={!draft.blocks.length || preview.loading} size="small"
                  startIcon={<PlayArrowIcon />} sx={{ ...modalHeaderBtnSx, px: 1.5, py: 0.5, fontSize: '13px', fontWeight: 600 }}>
                  {t('audio_preview_button')}
                </Button>
              )}
              {preview.loading && <LoadingIndicator steps={SUMMARY_LOADING_STEPS} />}
            </Box>
            {preview.error && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{preview.error}</Typography>}
            <Collapse in={preview.showText && !!preview.text}>
              <Box sx={{ mt: 1.5, px: 2, py: 1.5, borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.05)', fontSize: '13px', lineHeight: 1.7, color: 'text.secondary', whiteSpace: 'pre-line' }}>
                {preview.text}
              </Box>
            </Collapse>
          </Section>

          {error && <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{error}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Button variant="contained" onClick={save} disabled={saving || missing.length > 0 || !model}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              {card ? t('audio_save_changes') : t('audio_save')}
            </Button>
            {missing.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('audio_missing', { fields: missing.map((f) => t(`audio_field_${f}`)).join(', ') })}
              </Typography>
            )}
          </Box>
          <AreDisclaimer sx={{ justifyContent: 'flex-start' }} />
        </Box>
      </Box>

      {voiceOpen && (
        <AudioBuilderVoice
          draft={{ ...draft, model }}
          cardId={card?.id}
          catalog={catalog}
          sources={sources}
          language={language}
          greeting={card ? t('audio_voice_greeting_edit', { name: card.name }) : undefined}
          quickStart={quickStart}
          onDraft={(next) => { setDraft(next); setPreview({ loading: false, audio: '', text: '', error: '', showText: false }); }}
          onSaved={(saved, created) => onSaved(saved, created)}
          onClose={() => setVoiceOpen(false)}
        />
      )}
    </Dialog>
  );
}
